/* eslint-disable @typescript-eslint/no-explicit-any */
import { OfferType, SearchProfile } from '@/types';
import { BaseFields } from '../../SearchProfileScreen';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { showToast } from '@/utils/showToast';
import { useDeleteSearchProfile, useUpdateSearchProfile } from '@/queris';
import { COLORS, PATHS, SORTING_OPTIONS } from '@/constants';
import { BtnBox, Container, FilterFieldsList, ProjectBadge, StyledButton } from './styled';
import { Box, Button, Divider, FormControlLabel, Switch, Typography } from '@mui/material';
import {
  CheckboxGreenIcon,
  DeleteIcon,
  MapPinIcon,
  PenIcon,
  ResetIcon,
  SPCalendarIcon,
  SPCategoryIcon,
  SPEnergyEfficiencyIcon,
  SPEnergyIndexIcon,
  SPEquipsIcon,
  SPLocationMapIcon,
  SPPriceIcon,
  SPRoomsIcon,
  SPSortingIcon,
  SPSpaceIcon,
  TextField,
} from '@/components';
import { SearchFilterField } from './SearchFilterField';
import { formatNumberForDisplay } from '@/common';
import { useRouter } from '@/config/i18n/navigation';

type SearchProfileCardProps = {
  data: SearchProfile;
  fieldBase?: BaseFields;
};

export const SearchProfileCard: FC<SearchProfileCardProps> = ({ data, fieldBase }) => {
  const t = useTranslations();

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(data.name);

  const namesIsEqual = name === data.name;

  const router = useRouter();

  const onSuccess = () => {
    const message = t('searchProfile.messages.updatedSuccessfully');
    showToast.success(message);
  };

  const onDeleteSuccess = () => {
    const message = t('searchProfile.messages.deletedSuccessfully');
    showToast.success(message);
  };

  const { mutate, isPending: isUpdatePending } = useUpdateSearchProfile(onSuccess);
  const { mutate: onDelete, isPending: isDeletePending } = useDeleteSearchProfile(onDeleteSuccess);

  const openEditing = () => setIsEditing(true);
  const closeEditing = () => setIsEditing(false);

  const deleteHandler = () => onDelete({ id: data.id });

  const updateNameHandler = useCallback(() => {
    mutate({
      updateObj: {
        id: data.id,
        name,
      },
    });

    closeEditing();
  }, [mutate, data.id, name]);

  const updateSubscribeHandler = useCallback(() => {
    mutate({
      updateObj: {
        id: data.id,
        subscribe: !data.subscribe,
      },
    });

    closeEditing();
  }, [mutate, data.id, data.subscribe]);

  const filteredFields = Object.entries(data.filters).reduce(
    (acc, [key, value]) => {
      const isNull = value === null;
      const isEmptyArray = Array.isArray(value) && value.length === 0;

      if (!isNull && !isEmptyArray) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  const isProject = !!data.filters.project;

  const goToSearchList = () => {
    const basePath = isProject ? PATHS.PROJECTS : PATHS.HOUSING;
    router.push(`/${basePath}?${PATHS.SEARCH_PROFILE}=${data.id}`);
  };

  const updateEditNameHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const resetNameHandler = () => {
    if (data.name !== name) {
      setName(data.name);
    }
    closeEditing();
  };

  const getTitles = useCallback(
    (codes: string[] | undefined, list: { code: string | number; title: string }[] | undefined) => {
      if (!codes || !list) return '';
      return codes
        .map((code) => list.find((item) => String(item.code) === String(code))?.title)
        .filter(Boolean)
        .join(', ');
    },
    [],
  );

  const categoryTitles = useMemo(
    () => getTitles(filteredFields.categories, fieldBase?.categories ?? undefined),
    [filteredFields.categories, fieldBase?.categories, getTitles],
  );

  const equipsTitles = useMemo(
    () => getTitles(filteredFields.customEquips, fieldBase?.equipment ?? undefined),
    [filteredFields.customEquips, fieldBase?.equipment, getTitles],
  );

  const countries = useMemo(
    () => getTitles(filteredFields.countries, fieldBase?.countries ?? undefined),
    [filteredFields.countries, fieldBase?.countries, getTitles],
  );

  const division = useMemo(
    () => getTitles(filteredFields.division, fieldBase?.division ?? undefined),
    [filteredFields.division, fieldBase?.division, getTitles],
  );

  const districts = useMemo(
    () => getTitles(filteredFields.districts, fieldBase?.districts ?? undefined),
    [filteredFields.districts, fieldBase?.districts, getTitles],
  );

  const filterFieldsConfig = [
    {
      icon: <SPCalendarIcon />,
      isShow: filteredFields?.offerType,
      value: filteredFields?.offerType === OfferType.SELL ? t('sell') : t('rent'),
    },
    {
      icon: <SPLocationMapIcon />,
      isShow: !!filteredFields.countries?.length,
      value: countries,
    },
    {
      icon: <SPLocationMapIcon />,
      isShow: !!filteredFields.division?.length,
      value: division,
    },
    {
      icon: <SPLocationMapIcon />,
      isShow: !!filteredFields.districts?.length,
      value: districts,
    },
    {
      icon: <MapPinIcon width={15} height={15} stroke="#D25D3E" />,
      isShow: !!filteredFields.locations?.length,
      value: filteredFields.locations?.map((loc: { name: string }) => loc.name).join(', ') ?? '',
    },
    {
      icon: <SPCategoryIcon />,
      isShow: !!filteredFields.categories?.length,
      value: categoryTitles,
    },
    {
      icon: <SPSpaceIcon />,
      isShow: !!filteredFields.spaceTo,
      value: `${formatNumberForDisplay(filteredFields?.spaceFrom)} - ${formatNumberForDisplay(filteredFields?.spaceTo)} m²`,
    },
    {
      icon: <SPRoomsIcon />,
      isShow: !!filteredFields.roomNumberTo,
      value: `${formatNumberForDisplay(filteredFields?.roomNumberFrom)} - ${formatNumberForDisplay(filteredFields?.roomNumberTo)} ${t('shortRooms')}`,
    },
    {
      icon: <SPPriceIcon />,
      isShow: !!filteredFields?.priceTo,
      value: `€ ${formatNumberForDisplay(filteredFields?.priceFrom)} - ${formatNumberForDisplay(filteredFields?.priceTo)}`,
    },
    {
      icon: <SPPriceIcon />,
      isShow: !!filteredFields.rentPriceTo,
      value: `€ ${formatNumberForDisplay(filteredFields?.rentPriceFrom)} - ${formatNumberForDisplay(filteredFields.rentPriceTo)}`,
    },
    {
      icon: <SPEquipsIcon />,
      isShow: !!filteredFields.customEquips?.length,
      value: equipsTitles,
    },
    {
      icon: <SPEnergyIndexIcon />,
      isShow: !!filteredFields.powerConsumptionClasses?.length,
      value: getTitles(
        filteredFields.powerConsumptionClasses,
        fieldBase?.powerConsumptionClasses ?? undefined,
      ),
    },
    {
      icon: <SPEnergyEfficiencyIcon />,
      isShow: !!filteredFields.energyClassFactors?.length,
      value: getTitles(
        filteredFields.energyClassFactors,
        fieldBase?.energyClassFactors ?? undefined,
      ),
    },
    {
      icon: <SPSortingIcon />,
      isShow: !!filteredFields?.sorting,
      value: SORTING_OPTIONS.find((e) => e.value === filteredFields?.sorting)?.label || '',
    },
  ];

  return (
    <Container>
      <Box sx={{ display: 'flex', gap: '12px' }}>
        <Box sx={{ width: '89%' }}>
          {isEditing ? (
            <TextField
              name="name"
              size="small"
              placeholder={t('searchProfile.modal.namePlaceholder')}
              defaultValue={data.name}
              sx={{ width: '100%', height: '34px', marginBottom: '12px' }}
              value={name}
              inputProps={{ maxLength: 255, sx: { height: '100%' } }}
              onChange={updateEditNameHandler}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                height: '34px',
                minWidth: 0,
              }}
            >
              <Typography
                fontSize={18}
                fontWeight={600}
                lineHeight={'126%'}
                textTransform={'capitalize'}
                color={COLORS['primary/600']}
                noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {data.name}
              </Typography>
              <ProjectBadge>
                {isProject ? t('navigation.projects') : t('navigation.housing')}
              </ProjectBadge>
            </Box>
          )}

          <FilterFieldsList>
            {filterFieldsConfig.map((field, i) => (
              <SearchFilterField key={i} {...field} />
            ))}
          </FilterFieldsList>

          <Button
            variant="outline-secondary"
            sx={{ height: '32px', mt: '10px' }}
            onClick={goToSearchList}
          >
            {t('searchProfile.buttons.showResults')}
          </Button>
        </Box>
        {isEditing ? (
          <BtnBox>
            <StyledButton
              disabled={isUpdatePending || name.length === 0 || namesIsEqual}
              onClick={updateNameHandler}
            >
              <CheckboxGreenIcon />
            </StyledButton>
            <StyledButton disabled={isUpdatePending} onClick={resetNameHandler}>
              <ResetIcon />
            </StyledButton>
          </BtnBox>
        ) : (
          <BtnBox>
            <StyledButton disabled={isUpdatePending} onClick={openEditing}>
              <PenIcon />
            </StyledButton>
            <StyledButton disabled={isDeletePending} onClick={deleteHandler}>
              <DeleteIcon />
            </StyledButton>
          </BtnBox>
        )}
      </Box>

      <Divider />

      <FormControlLabel
        sx={{ width: 'fit-content', mb: '4px' }}
        control={
          <Switch
            disabled={isUpdatePending}
            name="subscribe"
            checked={data.subscribe}
            onChange={updateSubscribeHandler}
            color="primary"
            sx={{
              marginLeft: '8px',
              '& .MuiSwitch-switchBase': {
                '&.Mui-checked': {
                  color: COLORS.white,
                  '& + .MuiSwitch-track': {
                    backgroundColor: COLORS['primary/500'],
                    opacity: 0.8,
                  },
                },
              },
              '& .MuiSwitch-track': {
                backgroundColor: COLORS['neutral/500'],
              },
            }}
          />
        }
        label={t('searchProfile.modal.isSubscribe')}
      />
    </Container>
  );
};
